import { useEffect, useState, useRef, useCallback } from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { Overview } from "../components/Overview";
import { useColumnNames, useColumns, useGlobalRequirements, useGlobalRequirementsActions, useEmployees, useRoadmapActions, useActiveBoardId, useScheduledTasks, useAllTasks, getGlobalTaskById } from "../stores";
import { recalculateSchedule } from "../stores";
import { globalTasksStore } from "../stores/globalTasksStore";
import { TaskModal } from "../components/tasks";
import { useTaskModalState } from "../hooks/useTaskModalState";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import type { OverviewSearchParams } from "../router";

export function OverviewPage() {
  useDocumentTitle('Overview – qtplan');
  const search = useSearch({ strict: false }) as OverviewSearchParams;
  const navigate = useNavigate();
  const boardId = useActiveBoardId();
  const columns = useColumnNames();
  const columnDefs = useColumns();
  const globalRequirements = useGlobalRequirements();
  const { addNewRequirement, updateRequirement, deleteRequirement } = useGlobalRequirementsActions();
  const employees = useEmployees();
  const scheduledTasks = useScheduledTasks();
  const { addTask, deleteTask, removeFromBoard, updateColumn } = useRoadmapActions();
  const [viewMode, setViewMode] = useState<'columns' | 'timeline'>(search.view || 'columns');
  const [addTaskDefaults, setAddTaskDefaults] = useState<{ column?: string }>({});
  const isInitialMount = useRef(true);

  // All tasks for task history navigation
  const tasks = useAllTasks();

  const modal = useTaskModalState();

  const handleAddTaskAction = useCallback((task: Parameters<typeof addTask>[0]) => {
    addTask(task);
    setTimeout(() => recalculateSchedule(), 0);
  }, [addTask]);

  const handleOpenAddTaskModal = useCallback((column?: string) => {
    setAddTaskDefaults({ column });
    modal.openCreate();
  }, [modal.openCreate]);

  const handleTaskClick = useCallback((taskId: string) => {
    modal.openEdit(taskId, tasks.map(t => t.id));
  }, [tasks, modal.openEdit]);

  const handleMoveTask = useCallback((taskId: string, newColumn: string) => {
    updateColumn(taskId, newColumn);
    setTimeout(() => recalculateSchedule(), 0);
  }, [updateColumn]);

  const handleCloseTaskModal = useCallback(() => {
    modal.close();
    setAddTaskDefaults({});
  }, [modal.close]);

  // Scroll to anchor on mount/change
  useEffect(() => {
    if (search.anchor) {
      // Try different anchor formats
      const possibleIds = [
        search.anchor,
        `task-${search.anchor}`,
        `req-hard-${search.anchor}`,
        `req-soft-${search.anchor}`,
      ];

      for (const id of possibleIds) {
        const element = document.getElementById(id);
        if (element) {
          // Small delay to ensure DOM is ready
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
          break;
        }
      }
    }
    isInitialMount.current = false;
  }, [search.anchor]);

  // Sync State -> URL when view mode changes
  const handleViewModeChange = (newMode: 'columns' | 'timeline') => {
    setViewMode(newMode);

    const newSearch: OverviewSearchParams = {};
    if (newMode !== 'columns') newSearch.view = newMode;
    if (search.anchor) newSearch.anchor = search.anchor;

    navigate({
      to: "/boards/$boardId/overview",
      params: { boardId },
      search: newSearch,
      replace: true,
    });
  };

  return (
    <>
      <Overview
        columns={columns}
        columnDefs={columnDefs}
        employees={employees}
        globalRequirements={globalRequirements}
        onAddGlobalRequirement={addNewRequirement}
        onUpdateGlobalRequirement={updateRequirement}
        onDeleteGlobalRequirement={deleteRequirement}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        onAddTask={handleOpenAddTaskModal}
        onTaskClick={handleTaskClick}
        onMoveTask={handleMoveTask}
      />
      <TaskModal
        isOpen={modal.isOpen}
        onClose={handleCloseTaskModal}
        mode={modal.mode}
        taskId={modal.taskId}
        taskHistory={modal.taskHistory}
        historyIndex={modal.historyIndex}
        onNavigate={modal.navigate}
        onAdd={handleAddTaskAction}
        onUpdate={(task) => {
          globalTasksStore.send({ type: 'task.updateTitle', taskId: task.id, title: task.title });
          globalTasksStore.send({ type: 'task.updateStartDate', taskId: task.id, startDate: task.startDate });
          globalTasksStore.send({ type: 'task.updateCategories', taskId: task.id, categoryIds: task.categoryIds ?? [] });
          globalTasksStore.send({ type: 'task.updateDescription', taskId: task.id, description: Array.isArray(task.description) ? task.description : undefined });
          globalTasksStore.send({ type: 'task.updateConsequence', taskId: task.id, consequence: Array.isArray(task.consequence) ? task.consequence : undefined });
          globalTasksStore.send({ type: 'task.updateRequiredSkills', taskId: task.id, requiredSkills: typeof task.requiredSkills === 'string' ? task.requiredSkills : undefined });
          globalTasksStore.send({ type: 'task.updateEstimateMode', taskId: task.id, estimateMode: task.estimateMode });
          globalTasksStore.send({ type: 'task.updateEstimates', taskId: task.id, defaultEstimate: task.defaultEstimate, estimates: task.estimates });
          if (task.column) {
            updateColumn(task.id, task.column);
          }
          recalculateSchedule();
        }}
        getTaskById={(tId) => {
          const boardTask = tasks.find(t => t.id === tId);
          if (boardTask) return boardTask;
          const globalTask = getGlobalTaskById(tId);
          return globalTask ? { ...globalTask, column: '' } : undefined;
        }}
        getScheduledTask={(tId) => scheduledTasks.get(tId)}
        columns={columns}
        defaultColumn={addTaskDefaults.column}
        onDelete={(taskId) => { deleteTask(taskId); setTimeout(() => recalculateSchedule(), 0); }}
        onRemoveFromBoard={(taskId) => { removeFromBoard(taskId); setTimeout(() => recalculateSchedule(), 0); }}
      />
    </>
  );
}
